import React, { useCallback, useEffect, useState } from "react";
import { getConfigs, updateConfig } from "../../api/apiServiceFetch";
import "./Config.scss";

const FIELD_LABELS = {
	works: "Works",
	family: "Family (лимит знакомств)",
	swipes: "Свайпы",
	purchases: "Покупки",
	faceMatchAutoApprovePercent: "Авто-приём фото при совпадении ≥ %",
	faceMatchAutoRejectPercent: "Авто-отказ фото при совпадении < %",
};

const clampPercent = (value) => {
	const n = Number(value);
	if (!Number.isFinite(n)) return 0;
	return Math.min(100, Math.max(0, Math.round(n)));
};

const Config = () => {
	const [rows, setRows] = useState([]);
	const [drafts, setDrafts] = useState({});
	const [loading, setLoading] = useState(true);
	const [savingId, setSavingId] = useState(null);
	const [error, setError] = useState("");
	const [savedAt, setSavedAt] = useState(null);

	const loadAll = useCallback(async () => {
		setLoading(true);
		setError("");
		try {
			const data = await getConfigs();
			const list = Array.isArray(data) ? data : [];
			setRows(list);
			setDrafts({});
		} catch (err) {
			setError(err?.message || "Не удалось загрузить конфигурацию.");
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		void loadAll();
	}, [loadAll]);

	const draftFor = (row) => drafts[row.id] || row;

	const patchDraft = (id, patch) => {
		setDrafts((prev) => {
			const base = prev[id] || rows.find((r) => r.id === id) || {};
			return { ...prev, [id]: { ...base, ...patch } };
		});
	};

	const isDirty = (row) => {
		const draft = drafts[row.id];
		if (!draft) return false;
		return (
			!!draft.works !== !!row.works ||
			Number(draft.family) !== Number(row.family) ||
			Number(draft.swipes) !== Number(row.swipes) ||
			!!draft.purchases !== !!row.purchases ||
			clampPercent(draft.faceMatchAutoApprovePercent) !==
				clampPercent(row.faceMatchAutoApprovePercent) ||
			clampPercent(draft.faceMatchAutoRejectPercent) !==
				clampPercent(row.faceMatchAutoRejectPercent)
		);
	};

	const saveRow = async (row) => {
		const draft = draftFor(row);

		const family = Number(draft.family);
		const swipes = Number(draft.swipes);
		const faceMatchAutoApprovePercent = clampPercent(
			draft.faceMatchAutoApprovePercent,
		);
		const faceMatchAutoRejectPercent = clampPercent(
			draft.faceMatchAutoRejectPercent,
		);

		if (!Number.isFinite(family) || family < 0) {
			setError("Family должно быть числом ≥ 0.");
			return;
		}
		if (!Number.isFinite(swipes) || swipes < 0) {
			setError("Swipes должно быть числом ≥ 0.");
			return;
		}
		if (
			faceMatchAutoApprovePercent > 0 &&
			faceMatchAutoRejectPercent > 0 &&
			faceMatchAutoRejectPercent >= faceMatchAutoApprovePercent
		) {
			setError(
				"Порог авто-отказа должен быть строго меньше порога авто-приёма.",
			);
			return;
		}

		try {
			setSavingId(row.id);
			setError("");
			await updateConfig({
				id: row.id,
				works: !!draft.works,
				family,
				swipes,
				purchases: !!draft.purchases,
				faceMatchAutoApprovePercent,
				faceMatchAutoRejectPercent,
			});
			setSavedAt(Date.now());
			await loadAll();
		} catch (err) {
			setError(err?.message || "Не удалось сохранить.");
		} finally {
			setSavingId(null);
		}
	};

	return (
		<div className="config-page">
			<header className="config-page__header">
				<h2>Конфигурация</h2>
				<p>
					Глобальные настройки приложения из таблицы <code>dbo.config</code>.
					Только редактирование — добавление и удаление недоступны.
				</p>
			</header>

			{error ? (
				<div className="config-page__state config-page__state--error">
					{error}
				</div>
			) : null}

			{savedAt && !error ? (
				<div className="config-page__state config-page__state--ok">
					Сохранено
				</div>
			) : null}

			<div className="config-page__list">
				{loading ? (
					<div className="config-page__empty">Загружаем...</div>
				) : rows.length === 0 ? (
					<div className="config-page__empty">
						Записей в таблице config нет. Добавьте строку через БД.
					</div>
				) : (
					rows.map((row) => {
						const draft = draftFor(row);
						return (
							<div className="config-card" key={row.id}>
								<div className="config-card__head">
									<strong>Config #{row.id}</strong>
								</div>

								<div className="config-card__grid">
									<label className="config-card__field">
										<span>{FIELD_LABELS.works}</span>
										<label className="config-toggle">
											<input
												type="checkbox"
												checked={!!draft.works}
												disabled={savingId === row.id}
												onChange={(event) =>
													patchDraft(row.id, { works: event.target.checked })
												}
											/>
											<span>{draft.works ? "вкл" : "выкл"}</span>
										</label>
									</label>

									<label className="config-card__field">
										<span>{FIELD_LABELS.family}</span>
										<input
											type="number"
											min="0"
											step="1"
											value={draft.family ?? 0}
											disabled={savingId === row.id}
											onChange={(event) =>
												patchDraft(row.id, { family: event.target.value })
											}
										/>
									</label>

									<label className="config-card__field">
										<span>{FIELD_LABELS.swipes}</span>
										<input
											type="number"
											min="0"
											step="1"
											value={draft.swipes ?? 0}
											disabled={savingId === row.id}
											onChange={(event) =>
												patchDraft(row.id, { swipes: event.target.value })
											}
										/>
									</label>

									<label className="config-card__field">
										<span>{FIELD_LABELS.purchases}</span>
										<label className="config-toggle">
											<input
												type="checkbox"
												checked={!!draft.purchases}
												disabled={savingId === row.id}
												onChange={(event) =>
													patchDraft(row.id, {
														purchases: event.target.checked,
													})
												}
											/>
											<span>{draft.purchases ? "вкл" : "выкл"}</span>
										</label>
									</label>
									<label className="config-card__field">
										<span>{FIELD_LABELS.faceMatchAutoApprovePercent}</span>
										<input
											type="number"
											min="0"
											max="100"
											step="1"
											value={draft.faceMatchAutoApprovePercent ?? 0}
											disabled={savingId === row.id}
											onChange={(event) =>
												patchDraft(row.id, {
													faceMatchAutoApprovePercent: event.target.value,
												})
											}
										/>
										<small>0 — авто-приём выключен</small>
									</label>

									<label className="config-card__field">
										<span>{FIELD_LABELS.faceMatchAutoRejectPercent}</span>
										<input
											type="number"
											min="0"
											max="100"
											step="1"
											value={draft.faceMatchAutoRejectPercent ?? 0}
											disabled={savingId === row.id}
											onChange={(event) =>
												patchDraft(row.id, {
													faceMatchAutoRejectPercent: event.target.value,
												})
											}
										/>
										<small>0 — авто-отказ выключен</small>
									</label>
								</div>

								<div className="config-card__actions">
									<button
										type="button"
										disabled={savingId === row.id || !isDirty(row)}
										onClick={() => saveRow(row)}
									>
										{savingId === row.id ? "Сохраняем..." : "Сохранить"}
									</button>
								</div>
							</div>
						);
					})
				)}
			</div>
		</div>
	);
};

export default Config;
